// See cropperjs.md in this folder for a copy of the documentation at the time.

import React, { Component } from 'react';
import CropperJs from 'cropperjs';
import autobind from 'class-autobind';
import $ from 'jquery';
import max from 'lodash/max';

import { didComponentValueUpdate } from 'zoomforth_zfutils/react_helpers.js';

const CROPPER_JS_VIEW_MODE_NO_RESTRICTIONS = 0;
const CROPPER_JS_VIEW_MODE_FIT_CANVAS = 1;

// We basically want it to update as soon as possible without interrupting anything else
const CROPPER_UPDATE_THROTTLE_MS = 0;

export function sanitizeCropperData(cropperJs) {
  const data = cropperJs.getData();
  const { x, y, width, height } = data;
  return {
    ...data,
    x: x,
    y: y,
    width: width,
    height: height,
  };
}

const CROPPER_JS_DEFAULT_ZOOM = 1;

export function percentZoomToCropperJsZoom(percentZoom) {
  if (!percentZoom) {
    return CROPPER_JS_DEFAULT_ZOOM;
  }

  return (percentZoom / 100);
}

export function cropperJsZoomToPercentZoom(cropperJsZoom) {
  return (cropperJsZoom) * 100;
}

function updateBorderRadius(borderRadius) {
  $(".cropper-view-box, .cropper-face").css("border-radius", `${borderRadius}%`);
}

class CropperJsWrapper extends Component {
  constructor(props) {
    super(props);

    this.onCropperUpdateThrottleTimer = null;

    autobind(this);
  }

  handleCropperImageRef(imageRef) {
    this.imageRef = imageRef;
    if (imageRef && !this.cropperJs) {
      this.initCropperJs();
    }
  }

  initCropperJs() {
    const _this = this;
    const {
      x, y,
      width, height,
      aspectRatio
    } = this.props;

    const options = {
      aspectRatio,
      checkCrossOrigin: false,
      viewMode: this.props.isOutOfBoundsAllowed ? CROPPER_JS_VIEW_MODE_NO_RESTRICTIONS : CROPPER_JS_VIEW_MODE_FIT_CANVAS,
      dragMode: 'move',
      data: {
        x, y,
        width, height
      },
      preview: this.props.previewRefs || [],
      crop: this.handleCrop,
      ready() {
        _this.handleReady(this.cropper);
      }
    };

    const shouldAutoCropArea = !(x && y && width && height);
    if(shouldAutoCropArea){
      options.autoCropArea = 1;
    }

    new CropperJs(this.imageRef, options);
  };

  handleCrop() {
    window.clearTimeout(this.onCropperUpdateThrottleTimer);
    this.onCropperUpdateThrottleTimer = setTimeout(() => {
      const { x, y, width, height } = sanitizeCropperData(this.cropperJs);
      this.props.onCropperUpdate({ x, y, width, height });
    }, CROPPER_UPDATE_THROTTLE_MS);
  }

  handleReady(cropperJs) {
    this.cropperJs = cropperJs;
    updateBorderRadius(this.props.borderRadius);
    const { naturalWidth, naturalHeight } = cropperJs.getImageData(); // required for SVG sizing
    const {
      initX,
      initY,
      initWidth,
      initHeight,
      isOutOfBoundsAllowed,
      hasFinishedInitializing
    } = this.props;

    if(!hasFinishedInitializing){
      if(isOutOfBoundsAllowed){
        cropperJs.zoomTo(0.5);
        this.cropperJs.setData({
          x: initX,
          y: initY,
          width: initWidth,
          height: initHeight
        });
      }else{
        if(initX + initWidth > naturalWidth || initY + initHeight > naturalHeight){
          this.props.onCropperUpdate({
            isOutOfBoundsAllowed: true // this will reinitialize the cropper
          });
          return;
        }
      }

      if(this.props.hasCompressionChanged){
        this.handleImageChange(naturalWidth, naturalHeight);
      }

      this.props.onCropperUpdate({
        originalImageWidth: naturalWidth,
        originalImageHeight: naturalHeight
      });
    }

    this.imageRef.addEventListener('zoom', this.handleZoomListen());
    // Trigger the zoom listener so that it updates the wrapping component with the zoom value
    cropperJs.zoom(0);

    this.props.onReady();
  }

  // If the image has changed we need to recalculate the crops
  handleImageChange(naturalWidth, naturalHeight){
    if(this.props.originalImageWidth && this.props.originalImageHeight){
      const widthRatioChange = naturalWidth / this.props.originalImageWidth;
      const heightRatioChange = naturalHeight / this.props.originalImageHeight;
      const {
        initX,
        initY,
        initWidth,
        initHeight
      } = this.props;

      const newX = initX * widthRatioChange;
      const newWidth = initWidth * widthRatioChange;
      const newY = initY * heightRatioChange;
      const newHeight = initHeight * heightRatioChange;

      this.cropperJs.setData({ 
        x: newX,
        y: newY, 
        width: newWidth,
        height: newHeight
      });
      this.props.onCropperUpdate({
        x: newX,
        y: newY, 
        width: newWidth,
        height: newHeight
      });
    }
  }

  handleZoomListen() {
    let isRunning = false;

    return (e) => {
      if (isRunning == true) {
        // This particular call has been triggered by the manual zoomTo() call in this function. 
        // With this flag we can avoid infinite loops where the zoomTo() function will call itself indefinitely.
        isRunning = false;

        // We do not prevent the default from running here since we need the default event to run for the zoom to happen.
      } else {
        const { x, y, width, height } = this.cropperJs.getData();
        if (0 == max([x, y, width, height])) {
          // this is being run during initialization and our special logic should not run
        } else {
          isRunning = true;
          e.preventDefault();

          const zoom = e.detail.ratio;
          const data = this.cropperJs.getData();
          // This clears the selection and then zooms, which prevents zoom from being limited by the current selection.
          this.cropperJs.clear().zoomTo(zoom)
            // This adds the selection back to the cropper again
            .crop()
            // Since zoom changes the selection by default we reset it back to the original parameters after zoom.
            .setData(data);

          // Send the new values to the wrapping component
          this.props.onCropperUpdate({ zoom: cropperJsZoomToPercentZoom(zoom), x, y, width, height });
        }
      }
    }
  }

  destroyCropperJs() {
    this.imageRef.removeEventListener('zoom', this.handleZoomListen());
    this.cropperJs.destroy();
  }

  reInitCropperJs() {
    this.destroyCropperJs();
    this.initCropperJs();
  }

  componentDidUpdate(prevProps) {
    if (this.cropperJs) {
      const {
        x, y,
        width, height,
        isOutOfBoundsAllowed,
        previewRefs,
        borderRadius,
        zoom
      } = this.props;
      if (
        didComponentValueUpdate(isOutOfBoundsAllowed, prevProps.isOutOfBoundsAllowed)
        || didComponentValueUpdate(previewRefs, prevProps.previewRefs)
      ) {
        // Some prop changes requires re-initializing the cropper
        this.reInitCropperJs();
        // re-initializing the cropper also updates all other values so no need to check the other conditionals
        return;
      }

      if (didComponentValueUpdate(borderRadius, prevProps.borderRadius)) {
        updateBorderRadius(this.props.borderRadius);
      }

      if (
        didComponentValueUpdate(x, prevProps.x)
        || didComponentValueUpdate(y, prevProps.y)
        || didComponentValueUpdate(width, prevProps.width)
        || didComponentValueUpdate(height, prevProps.height)
      ) {
        this.cropperJs.setData({ x, y, width, height });
      }

      if (didComponentValueUpdate(zoom, prevProps.zoom)) {
        this.cropperJs.zoomTo(percentZoomToCropperJsZoom(zoom));
      }
    }
  }

  render() {
    const {
      className,
      originalImageSrc
    } = this.props;

    return (
      <div className={className} style={{ height: "100%" }}>
        <img
          ref={this.handleCropperImageRef}
          src={originalImageSrc}
          style={{ display: "block", maxWidth: "100%" }}
        />
      </div>
    );
  }
}

export default CropperJsWrapper;
